import { Link } from "react-router-dom";
import { Phone, MessageCircle, ArrowRight, Star } from "lucide-react";
import { COMPANY } from "../../lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758273238415-01ec03d9ef27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBob21lJTIwY2xlYW5pbmclMjBzZXJ2aWNlfGVufDB8fHx8MTc3NzExMTg5N3ww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const waLink = `https://wa.me/${COMPANY.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(
    "Hi M Brothers, I'd like a free cleaning quote."
  )}`;

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 lg:pt-40 pb-20 md:pb-28 overflow-hidden bg-mesh grain-overlay"
    >
      {/* Background ornaments */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-blue-300/30 blur-3xl animate-blob" />
        <div className="absolute top-20 right-0 w-[460px] h-[460px] rounded-full bg-sky-300/30 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-indigo-300/20 blur-3xl animate-blob" style={{ animationDelay: "6s" }} />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <div className="animate-fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide uppercase">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Limited Availability — Reserve Your Spot
            </div>

            <h1
              data-testid="hero-title"
              className="animate-fade-up delay-100 mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight font-light text-slate-900 leading-[1.05]"
            >
              Premium <span className="font-medium gradient-text">Cleaning</span>
              <br className="hidden sm:block" /> Services Across Vancouver
            </h1>

            <p
              data-testid="hero-subtitle"
              className="animate-fade-up delay-200 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Professional, fast, and eco-friendly cleaning for homes and businesses —
              done right the first time. Trusted across {COMPANY.area}.
            </p>

            {/* CTAs */}
            <div className="animate-fade-up delay-300 mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all active:scale-[0.98] shadow-luxury hover:shadow-luxury-hover"
              >
                Get a Free Quote
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneClean}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-slate-900 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1EBE5D] transition-all active:scale-[0.98]"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>

            {/* Mini trust */}
            <div className="animate-fade-up delay-400 mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-2 text-sm text-slate-700 font-medium">5.0 Client Rating</span>
              </div>
              <div className="h-4 w-px bg-slate-200" />
              <span className="text-sm text-slate-600">Eco-Friendly · Insured · Vetted</span>
            </div>
          </div>

          {/* Right image */}
          <div className="lg:col-span-5 relative animate-fade-up delay-200">
            {/* Glow halo */}
            <div className="absolute -inset-6 bg-gradient-to-tr from-blue-500/30 via-sky-400/20 to-indigo-400/20 blur-3xl rounded-3xl pointer-events-none" />
            <div className="relative rounded-2xl overflow-hidden shadow-3d shadow-3d-hover transition-all duration-700 [transform-style:preserve-3d] hover:[transform:perspective(1400px)_rotateY(-4deg)_rotateX(2deg)]">
              <img
                src={HERO_IMG}
                alt="Professional cleaner mopping a bright modern living room"
                className="w-full h-[420px] sm:h-[520px] lg:h-[560px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
              {/* Inner highlight */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-white/40 rounded-2xl" />
            </div>

            {/* Floating card */}
            <div className="hidden sm:block absolute -left-4 lg:-left-10 bottom-6 bg-white/90 backdrop-blur-xl border border-white/60 rounded-xl shadow-3d p-4 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-sky-400 flex items-center justify-center text-white shadow-glow-soft">
                  <span className="font-display text-lg font-medium">M</span>
                </div>
                <div className="leading-tight">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Today</div>
                  <div className="text-sm font-medium text-slate-900">Same-day bookings open</div>
                </div>
              </div>
            </div>

            {/* Floating rating chip */}
            <div className="hidden md:flex absolute -right-3 lg:-right-6 top-8 items-center gap-2 bg-white/90 backdrop-blur-xl border border-white/60 rounded-full px-4 py-2 shadow-3d animate-float" style={{ animationDelay: "1.2s" }}>
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-semibold text-slate-900">5.0</span>
              <span className="text-xs text-slate-500">· Vancouver</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
